import { useMemo, useState } from 'react';
import { Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useViewShortfalls } from 'all-in-one-shared-api';

import { isLocaleEnglish } from 'utils/languageUtils';
import { RowSpaced } from 'pages/claims/components/styledComponents';
import { ClaimCard } from 'pages/claims/components';
import formatClaimCardData from 'pages/claims/utils/formatClaimCardData';
import { TitleRow } from 'components/styledComponents';
import { ButtonIcon, ButtonWithIcon, Header } from './components';
import { useNavigate } from 'react-router-dom';

import { useKeycloakReady } from 'hooks';
import { CustomPagination } from 'components';

const PAGE_SIZE = 6;

export const ShortfallPage = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { token, tokenParsed } = useKeycloakReady();
  const { data } = useViewShortfalls({ authToken: token, memberType: tokenParsed?.memberType });

  const claims = useMemo(() => {
    if (!data) return [];

    return data.results.map(formatClaimCardData(isLocaleEnglish(i18n.language)));
  }, [data, i18n.language]);

  const totalCount = data?.totalCount || 0;

  const goToSettleAll = () => {
    // TODO(Anson Kan): finish this when the settle all page is done
    navigate('settle');
  };

  const settleSingleShortfall = (id: string, sequenceNo: number) => {
    navigate({
      pathname: 'settle',
      search: `?claimId=${id}&sequenceNo=${sequenceNo}`,
    });
  };

  return (
    <>
      <TitleRow align="middle" justify="space-between">
        <Col>
          <Header />
        </Col>

        <Col>
          <ButtonWithIcon
            icon={<ButtonIcon name="Union" width={15} />}
            disabled={!claims.some((item) => item.shortfallFlag != 'Y')}
            onClick={goToSettleAll}
          >
            {t('claims.shortfall.settleAll')}
          </ButtonWithIcon>
        </Col>
      </TitleRow>

      {totalCount ? (
        <>
          {/* TODO(Anson Kan): abstract these spacing properties */}
          <RowSpaced gutter={[16, 16]} wrap>
            {claims
              .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
              .map(({ claimId, referenceNo, sequenceNo, ...props }) => (
                // TODO(Anson Kan): abstract these responsive properties
                <Col key={`${claimId}-${referenceNo}-${sequenceNo}`} xs={24} sm={12} lg={8}>
                  <ClaimCard
                    claimId={claimId}
                    referenceNo={referenceNo}
                    sequenceNo={sequenceNo}
                    onSettleShortfall={settleSingleShortfall}
                    {...props}
                  />
                </Col>
              ))}
          </RowSpaced>
          <RowSpaced>
            <Col>
              <CustomPagination current={page} onChange={setPage} total={totalCount} defaultPageSize={PAGE_SIZE} />
            </Col>
          </RowSpaced>
        </>
      ) : (
        <div>{t('claims.shortfall.noShortfallFound')}</div>
      )}
    </>
  );
};
